<script lang="ts">
	import { format } from 'svelte-i18n';
	import { edit, resetEditUpdateStore, update } from 'src/stores/ui/edit_lists';
	import {
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Modal,
		Radio,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { profiles } from 'src/stores/features/profiles';
	import { user_data } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { Device } from 'src/types/Device';
	import {
		moveDevicesToUnitAction,
		transferOwnershipAction,
		unitOptions,
	} from 'src/stores/account/home';
	import TransferOwnershipCredentialsForm from 'src/components/Settings/HomeManagement/shared/TransferOwnershipCredentialsForm.svelte';
	import MoveDevicesDestinationForm from 'src/components/Settings/HomeManagement/shared/MoveDevicesDestinationForm.svelte';
	import {
		TRANSFER_HISTORY_NOT_MOVED_NOTICE_DEFAULT,
		TRANSFER_OWNERSHIP_FAILURE_HINT_BODY_DEFAULT,
		createBatchTransferCredentialsState,
		getActiveTransferPassword,
		isTransferSubmitDisabled,
		validateTransferForm,
		type TransferActionMode,
	} from 'src/components/Settings/HomeManagement/shared/transfer-ownership-helpers';
	import { buildFailureMessageHtml } from 'src/components/Settings/HomeManagement/shared/home-management-utils';
	import BluTransferGatewaySection from 'src/components/Settings/HomeManagement/shared/BluTransferGatewaySection.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { is_GBLE } from 'src/helpers/interface_helpers';

	type TransferTargetType = '' | 'account' | 'house';

	export let showModal: boolean;
	export let areFiltersDisabled: boolean;

	let transferEmail = '';
	let profileTransferPassword = '';
	let manualTransferPassword = '';
	let transferEmailError = '';
	let transferPasswordError = '';
	let isLoading = false;
	let showTransferFailureHelp = false;
	let transferServerErrorMessage = '';

	let actionMode: TransferActionMode = '';
	let selectedEmail = '';

	let targetType: TransferTargetType = '';
	let selectedUnitId = '';
	let selectedGatewayIds: string[] = [];

	$: credentialsVariant = $showNewLayout ? ('v2' as const) : ('legacy' as const);

	$: updatedProfiles = ($profiles ?? []).filter((profile) => profile.email !== $user_data.email);

	$: hasOtherHomes = $unitOptions.length > 0;

	$: activeTransferPassword = getActiveTransferPassword(
		actionMode,
		profileTransferPassword,
		manualTransferPassword,
	);

	$: isAccountSubmitDisabled = isTransferSubmitDisabled({
		actionMode,
		selectedEmail,
		transferEmail,
		profileTransferPassword,
		manualTransferPassword,
	});

	$: isHouseSubmitDisabled = !selectedUnitId;

	function computeSubmitDisabled(
		target: TransferTargetType,
		accountDisabled: boolean,
		houseDisabled: boolean,
	): boolean {
		if (target === 'house') return houseDisabled;
		if (target === 'account') return accountDisabled;
		return true;
	}

	$: isSubmitDisabled = computeSubmitDisabled(
		targetType,
		isAccountSubmitDisabled,
		isHouseSubmitDisabled,
	);

	$: composedFailureMessageHtml = !showTransferFailureHelp
		? ''
		: buildFailureMessageHtml(
				$format('transfer_ownership_failure_hint_body', {
					default: TRANSFER_OWNERSHIP_FAILURE_HINT_BODY_DEFAULT,
				}),
				transferServerErrorMessage,
				$format('transfer_ownership_server_response_label', {
					default: 'Server response:',
				}),
			);

	function resolveInitialTargetType(): TransferTargetType {
		if (hasOtherHomes) return '';
		return 'account';
	}

	function applyTransferFormReset(): void {
		const cred = createBatchTransferCredentialsState($profiles, $user_data?.email);
		actionMode = cred.actionMode;
		selectedEmail = cred.selectedEmail;
		transferEmail = cred.transferEmail;
		profileTransferPassword = cred.profileTransferPassword;
		manualTransferPassword = cred.manualTransferPassword;
		transferEmailError = cred.transferEmailError;
		transferPasswordError = cred.transferPasswordError;
		showTransferFailureHelp = cred.showTransferFailureHelp;
		transferServerErrorMessage = cred.transferServerErrorMessage;
		selectedUnitId = '';
		selectedGatewayIds = [];
		targetType = resolveInitialTargetType();
	}

	let prevShowModal = false;
	$: {
		if (showModal && !prevShowModal) {
			applyTransferFormReset();
		}
		prevShowModal = showModal;
	}

	function validateForm(): boolean {
		const { emailError, passwordError, valid } = validateTransferForm({
			actionMode,
			selectedEmail,
			transferEmail,
			password: activeTransferPassword,
			formatFn: $format,
		});
		transferEmailError = emailError;
		transferPasswordError = passwordError;
		return valid;
	}

	function getSelectedDeviceIds(): string[] {
		const selectedDevices: Device[] = Object.values($update.selectedDevices) as Device[];
		return selectedDevices.map((d) => d.id);
	}

	function buildTransferDeviceIds(): string[] {
		const deviceIds = getSelectedDeviceIds();
		return [...new Set([...deviceIds, ...selectedGatewayIds])];
	}

	$: selectedBluDevices = (Object.values($update.selectedDevices ?? {}) as Device[]).filter(
		(device) => is_GBLE(device) && !device.shared,
	);
	$: showBluGatewaySection = selectedBluDevices.length > 0 && targetType !== '';

	async function confirmTransfer(): Promise<void> {
		if (!validateForm()) return;

		const deviceIds = buildTransferDeviceIds();
		if (deviceIds.length === 0) return;

		const recipientEmail = actionMode === 'profile' ? selectedEmail : transferEmail;

		showTransferFailureHelp = false;
		transferServerErrorMessage = '';

		isLoading = true;

		try {
			const result = await transferOwnershipAction(
				deviceIds,
				recipientEmail,
				activeTransferPassword,
			);
			if (!result.isok) {
				toast.open({
					type: 'danger',
					title: $format('error_transfer_device_owner', {
						default: 'Failed to transfer device ownership.',
					}),
					duration: 2000,
				});
				showTransferFailureHelp = true;
				transferServerErrorMessage = result.errorMessage ?? '';
				return;
			}

			toast.open({
				type: 'success',
				title: $format('success_transfer_device_owner', {
					default: 'Device ownership transferred successfully.',
				}),
				duration: 2000,
			});
			resetEditUpdateStore();
			setSidePanelState({ open: false });
			areFiltersDisabled = false;
			showModal = false;
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLoading = false;
		}
	}

	async function confirmMove(): Promise<void> {
		const deviceIds = buildTransferDeviceIds();
		if (deviceIds.length === 0 || !selectedUnitId) return;

		isLoading = true;

		try {
			const result = await moveDevicesToUnitAction(deviceIds, Number(selectedUnitId));
			if (!result.isok) {
				toast.open({
					type: 'danger',
					title: $format('error_move_devices_to_unit', {
						default: 'Failed to move devices.',
					}),
					duration: 2000,
				});
				return;
			}

			toast.open({
				type: 'success',
				title: $format('success_move_devices_to_unit', {
					default: 'Devices moved successfully.',
				}),
				duration: 2000,
			});
			resetEditUpdateStore();
			setSidePanelState({ open: false });
			areFiltersDisabled = false;
			showModal = false;
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLoading = false;
		}
	}

	function handleSubmit(): void {
		if (targetType === 'house') {
			void confirmMove();
		} else {
			void confirmTransfer();
		}
	}

	function selectAccountTarget(): void {
		targetType = 'account';
		selectedUnitId = '';
	}

	function selectHouseTarget(): void {
		targetType = 'house';
		actionMode = '';
		selectedEmail = '';
		transferEmail = '';
		profileTransferPassword = '';
		manualTransferPassword = '';
		transferEmailError = '';
		transferPasswordError = '';
		showTransferFailureHelp = false;
		transferServerErrorMessage = '';
	}

	$: selectedDeviceCount = Object.keys($update.selectedDevices ?? {}).length;

	const onClose = (): void => {
		if (!$edit) resetEditUpdateStore();
		showTransferFailureHelp = false;
		transferServerErrorMessage = '';
		showModal = false;
	};
</script>

<Modal
	on:close={onClose}
	{showModal}
	title={capFirstChar($format(`sub_settings_title_${$update.type}`))}
	removeScrollPadding
>
	<div class="pl-m pr-m" slot="content">
		{#if $showNewLayout}
			<AlertV2
				class="mb-m"
				heading={$format('transfer_history_not_moved_notice', {
					default: TRANSFER_HISTORY_NOT_MOVED_NOTICE_DEFAULT,
				})}
			/>
		{:else}
			<Alert
				class="mb-m"
				type="info"
				heading={$format('transfer_history_not_moved_notice', {
					default: TRANSFER_HISTORY_NOT_MOVED_NOTICE_DEFAULT,
				})}
			/>
		{/if}
		<div class="separator mb-m"></div>

		{#if hasOtherHomes}
			<Text element="h3" styleType="body-small" class="mb-s">
				{$format('text_transfer_target_picker_title', {
					default: 'Where do you want to send this device?',
				})}
			</Text>
			<div class="mb-s">
				<Radio
					size="m"
					id="batch-transfer-target-account"
					value="account"
					selected={targetType}
					on:click={selectAccountTarget}
				>
					{$format('radio_transfer_target_account', {
						default: 'To another account',
					})}
				</Radio>
			</div>
			<div>
				<Radio
					size="m"
					id="batch-transfer-target-house"
					value="house"
					selected={targetType}
					on:click={selectHouseTarget}
				>
					{$format('radio_transfer_target_home', {
						default: 'To another home in this account',
					})}
				</Radio>
			</div>
			{#if targetType !== ''}
				<div class="separator mt-m mb-m"></div>
			{/if}
		{/if}

		{#if showBluGatewaySection}
			<BluTransferGatewaySection
				bluDevices={selectedBluDevices}
				transferDeviceIds={getSelectedDeviceIds()}
				bind:selectedGatewayIds
				variant={credentialsVariant}
			/>
			<div class="separator mt-m mb-m"></div>
		{/if}

		{#if targetType === 'account'}
			<TransferOwnershipCredentialsForm
				formatFn={$format}
				variant={credentialsVariant}
				{updatedProfiles}
				getProfileRadioId={(email) =>
					`radio-transfer-batch-${email.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
				manualEmailInputId={$showNewLayout
					? 'batch-transfer-owner-email-v2'
					: 'batch-transfer-owner-email-legacy'}
				manualPasswordInputId={$showNewLayout
					? 'batch-transfer-owner-password-v2'
					: 'batch-transfer-owner-password-legacy'}
				bind:actionMode
				bind:selectedEmail
				bind:transferEmail
				bind:profileTransferPassword
				bind:manualTransferPassword
				bind:transferEmailError
				bind:transferPasswordError
				{showTransferFailureHelp}
				{composedFailureMessageHtml}
			/>
		{:else if targetType === 'house'}
			<MoveDevicesDestinationForm
				bind:selectedUnitId
				selectedCount={selectedDeviceCount}
				idPrefix="batch-transfer-unit"
			/>
		{/if}
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if $showNewLayout}
			<ButtonV2 size="l" variant="secondary" class="mr-s" on:click={onClose}>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				size="l"
				variant="primary"
				disabled={isSubmitDisabled}
				loading={isLoading}
				on:click={handleSubmit}
			>
				{#if targetType === 'house'}
					{$format('button_move', { default: 'Move' })}
				{:else}
					{$format('button_transfer', { default: 'Transfer' })}
				{/if}
			</ButtonV2>
		{:else}
			<Button
				width="m"
				variant="secondary"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				on:click={onClose}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				width="m"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				disabled={isSubmitDisabled}
				loading={isLoading}
				on:click={handleSubmit}
				aria-label={targetType === 'house'
					? $format('button_move', { default: 'Move' })
					: $format('button_transfer', { default: 'Transfer' })}
			>
				{#if targetType === 'house'}
					{$format('button_move', { default: 'Move' })}
				{:else}
					{$format('button_transfer', { default: 'Transfer' })}
				{/if}
			</Button>
		{/if}
	</div>
</Modal>

<style>
	.separator {
		border-top: 1px solid var(--color-border, #212128);
	}
</style>
